/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef, useCallback } from 'react';

import AppTable from 'components/appTable';
import PanelBoxLayout from 'components/layout/panelBox';
import Toolbar, { ToolbarRight } from 'components/toolbar';
import Empty from 'components/empty';
import { useActions } from 'common/hooks/useActions';
import TicketFilterPopup from '../filterForm';
import RowAction from 'components/rowAction';

import StatusChip from 'components/statusChip';
import Value from 'components/value';
import { formatDateTime, MISSING } from 'common/helpers/format';

import * as S from './styles';
import * as T from 'common/types';

const PAGE_SIZE = 50;

interface IProps {
  data: any[];
  loading: boolean;
  hasMore?: boolean;
  infiniteScroll?: boolean;
}

export default function TicketTable({ data, loading, infiniteScroll }: IProps) {
  const { ticketsRequestActionNew } = useActions();
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    status: ''
  });
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const observer = useRef<IntersectionObserver | null>(null);

  const lastItem = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && infiniteScroll && hasMore && !loading) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, infiniteScroll]
  );

  const fetchData = () => {
    ticketsRequestActionNew(meta, page, PAGE_SIZE);
  };

  useEffect(() => {
    if (data?.length < PAGE_SIZE) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }
  }, [data]);

  useEffect(() => {
    if (infiniteScroll) fetchData();
  }, [page, meta]);

  useEffect(() => {
    // if (Array.isArray(data)) {
    //   if (page === 1 && meta.status === '') {
    //     setReqData(data);
    //     return;
    //   }

    //   if (meta.status === '') {
    //     setReqData((prev) => [...prev, ...data]);
    //     return;
    //   }

    // }
    setFilteredData((prev) => [...data]);
  }, [data]);

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <Toolbar>
          {/* Search is switched off on this screen — the endpoint takes no
              query. See FINDINGS. */}
          <ToolbarRight>
            <TicketFilterPopup setFilter={setMeta} filter={meta} setPage={setPage} />
          </ToolbarRight>
        </Toolbar>
        <AppTable
          headers={[
            { id: 1, key: 'name', title: 'Name', dir: 'left', width: 130 },
            { id: 2, key: 'category', title: 'Category', dir: 'left', width: 160 },
            { id: 3, key: 'description', title: 'Description', dir: 'left' },
            { id: 4, key: 'createdAt', title: 'Open Date', dir: 'left', width: 156 },
            { id: 7, key: 'priority', title: 'Priority', dir: 'left', width: 100 },
            { id: 8, key: 'status', title: 'Status', dir: 'left', width: 140 },
            { id: 9, key: 'edit', title: '', type: 'actions', width: 64 }
          ]}
          lastItemRef={lastItem}
          loading={loading}
          data={filteredData}
          empty={
            <Empty
              title={meta.status ? 'No tickets with that status' : 'No tickets yet'}
              text={
                meta.status ? 'Clear the filter to see every ticket.' : 'When someone raises a request it appears here.'
              }
            />
          }
          altanfeethiFields={{
            /*
             * These three used to cut their value at fifteen characters in JS
             * and glue on an ellipsis. AppTable already clips a cell that does
             * not fit and puts the whole value on the cell's title attribute
             * when it does — so the hand-rolled version truncated earlier than
             * necessary, at the same fifteen characters whether the column was
             * 120px or 400px, and threw away the tooltip on the way.
             */
            name: (item: T.Pages.TicketList) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.provided}>{item?.user?.displayName}</Value>
              </S.Div>
            ),
            category: (item: T.Pages.TicketList) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.none}>{item?.category?.title}</Value>
              </S.Div>
            ),
            description: (item: T.Pages.TicketList) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.provided}>{item?.description?.replace(/\r\n/g, ' ')}</Value>
              </S.Div>
            ),
            createdAt: (item: T.Pages.TicketList) => (
              <S.Div className="text-left">
                <Value mono>{formatDateTime(item?.createdAt)}</Value>
              </S.Div>
            ),
            priority: (item: T.Pages.TicketList) => (
              <S.Div className="text-left">
                <StatusChip domain="ticketPriority" value={item?.priority} fallback={<Value />} />
              </S.Div>
            ),
            status: (item: T.Pages.TicketList) => (
              <S.Div className="text-left">
                <StatusChip domain="ticketStatus" value={item?.status} fallback={<Value />} />
              </S.Div>
            ),
            /*
             * The design's row action: a quiet icon, not a full outline button
             * repeated down every row. The same eye the orders list already
             * uses for the same act, so "open this record" looks like one
             * thing across the product.
             */
            edit: (item: T.Pages.TicketList) => (
              <RowAction to={`/panel/ticket/tickets/${item?.id}`} label="Open ticket">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </RowAction>
            )
          }}
        />
      </PanelBoxLayout>
      {/* {data.totalPages ? (
        <PaginationApp
          page={meta.page}
          count={meta.page_size * data.totalPages}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={data.totalPages}
          pageSize={meta.page_size}
          handleChangeTake={handleChangePageSize}
        />
      ) : (
        <></>
      )} */}
    </S.Content>
  );
}
